import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LayoutDashboard, ClipboardCheck, ListChecks, Banknote, Layers, Tags, FileBarChart, History, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { oapi } from '@/lib/officialsApi';
import { Loading } from './PayBits';
import { PayOverview } from './PayOverview';
import { VerifyQueue } from './VerifyQueue';
import { LedgerTable } from './LedgerTable';
import { PaymentsHistory } from './PaymentsHistory';
import { BatchesPanel } from './BatchesPanel';
import { RatesPanel } from './RatesPanel';
import { ReportsPanel } from './ReportsPanel';
import { AuditPanel } from './AuditPanel';
import { PaySettingsPanel } from './PaySettingsPanel';
import { RecordPaymentDialog } from './RecordPaymentDialog';
import { AdjustmentDialog } from './AdjustmentDialog';
import { OfficialLedgerDialog } from './OfficialLedgerDialog';

const VIEWS = [
  ['overview', 'Overview', LayoutDashboard], ['verify', 'Verify', ClipboardCheck], ['ledger', 'Ledger', ListChecks], ['payments', 'Payments', Banknote],
  ['batches', 'Batches', Layers], ['rates', 'Rates', Tags], ['reports', 'Reports', FileBarChart], ['audit', 'Audit trail', History],
  ['settings', 'Settings', SlidersHorizontal],
];

const SubNav = ({ view, onView }) => (
  <div className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1" data-testid="pay-subnav">
    {VIEWS.map(([k, l, Icon]) => (
      <button key={k} type="button" onClick={() => onView(k)} data-testid={`pay-view-${k}`}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${view === k ? 'bg-[#00247D] text-white' : 'bg-white border text-slate-600 hover:border-slate-400'}`}>
        <Icon className="w-3.5 h-3.5" />{l}
      </button>
    ))}
  </div>
);

export const PaymentsTab = ({ orgId, officials, roles, competitions }) => {
  const [meta, setMeta] = useState(null);
  const [view, setView] = useState('overview');
  const [ledgerFilter, setLedgerFilter] = useState(null);
  const [tick, setTick] = useState(0);
  const [payFor, setPayFor] = useState(null);
  const [adjFor, setAdjFor] = useState(null);
  const [officialId, setOfficialId] = useState(null);
  const loadMeta = useCallback(() => oapi('/officials/payments/settings', { orgId }).then(setMeta).catch(e => toast.error(e.message)), [orgId]);
  useEffect(() => { loadMeta(); }, [loadMeta]);
  const refresh = useCallback(() => setTick(t => t + 1), []);
  const perms = useMemo(() => new Set(meta?.perms || []), [meta]);
  if (!meta) return <Loading />;
  const currency = meta.payments.currency;
  const go = (v, filter) => { setLedgerFilter(filter ? { ...filter } : null); setView(v); };
  const p = { orgId, currency, perms, competitions, officials, roles, tick, refresh, go, openOfficial: setOfficialId, recordPayment: setPayFor, addAdjustment: setAdjFor,
    budgets: meta.payments.budgets, reloadMeta: loadMeta };
  return (
    <div className="space-y-4" data-testid="officials-payments-tab">
      <SubNav view={view} onView={k => go(k)} />
      {view === 'overview' && <PayOverview {...p} />}
      {view === 'verify' && <VerifyQueue {...p} />}
      {view === 'ledger' && <LedgerTable {...p} initialFilter={ledgerFilter} />}
      {view === 'payments' && <PaymentsHistory {...p} />}
      {view === 'batches' && <BatchesPanel {...p} />}
      {view === 'rates' && <RatesPanel {...p} />}
      {view === 'reports' && <ReportsPanel {...p} />}
      {view === 'audit' && <AuditPanel {...p} />}
      {view === 'settings' && <PaySettingsPanel {...p} meta={meta} onSaved={(r) => { setMeta(r); refresh(); }} />}
      <RecordPaymentDialog target={payFor} orgId={orgId} currency={currency} officials={officials} onClose={() => setPayFor(null)} onDone={refresh} />
      <AdjustmentDialog target={adjFor} orgId={orgId} currency={currency} officials={officials} competitions={competitions} onClose={() => setAdjFor(null)} onDone={refresh} />
      <OfficialLedgerDialog officialId={officialId} orgId={orgId} currency={currency} perms={perms} tick={tick} refresh={refresh}
        onClose={() => setOfficialId(null)} recordPayment={setPayFor} addAdjustment={setAdjFor} />
    </div>
  );
};
